import { Card, Heading } from '@zillow/constellation';
import type MarketStats from 'app/shared/models/MarketStats';
import dateUtils from 'app/shared/utils/dateUtils';
import numberUtils from 'app/shared/utils/numberUtils';
import React, { useMemo } from 'react';
import QuestionAnswer from './QuestionAnswer';

interface MarketFAQProps {
    marketStats: MarketStats;
    isMobile: boolean;
}

function moreOrLess(amount: number | null): string | null {
    if (amount === null) {
        return null;
    }
    return amount >= 0 ? 'more' : 'less';
}

export function getFAQ(marketStats: MarketStats): Array<{ question: string; answer: string }> {
    if (marketStats.medianRent === null) {
        return [];
    }
    const {
        areaName,
        medianRent,
        minRent,
        maxRent,
        yearlyChange,
        monthlyChange,
        availableRentals,
        medianNationalRent
    } = marketStats;

    const displayRent = numberUtils.asCurrency(medianRent);
    const curMonth = dateUtils.currentMonth();
    const prevMonth = dateUtils.previousMonth();
    const curYear = dateUtils.currentYear();
    const prevYear = dateUtils.previousYear();

    return [
        {
            question: `What is the average rent in ${areaName}?`,
            answer: `The median rent in ${areaName} is <strong>${displayRent}.</strong>`
        },
        minRent !== null &&
            maxRent !== null && {
                question: `How much does it cost to rent a house in ${areaName}?`,
                answer: `Houses in ${areaName} rent between <strong>${numberUtils.asCurrency(
                    minRent
                )} - ${numberUtils.asCurrency(maxRent)}</strong> with a median rent of ${displayRent}.`
            },
        yearlyChange !== null && {
            question: `How has the rent in ${areaName} changed in the last year?`,
            answer: ` The median rent price in ${areaName} for ${curMonth} ${curYear} is ${displayRent}. This is <strong>${numberUtils.asCurrency(
                Math.abs(yearlyChange)
            )}</strong> ${moreOrLess(yearlyChange)} than ${curMonth} ${prevYear}.`
        },

        monthlyChange !== null && {
            question: `How much has the rent in ${areaName} increased/decreased in the last month?`,
            answer: `The median rent price in ${areaName} ${curMonth} ${curYear} is ${displayRent}. This is <strong>${numberUtils.asCurrency(
                Math.abs(monthlyChange)
            )}</strong> ${moreOrLess(monthlyChange)} than ${prevMonth}.`
        },

        availableRentals !== null && {
            question: `How many available rentals are there in ${areaName}?`,
            answer: `There are currently <strong>${numberUtils.fullNumber(
                availableRentals
            )}</strong> rentals available in ${areaName}.`
        },

        medianNationalRent !== null && {
            question: `How do rent prices in ${areaName} compare with the national average?`,
            answer: `The median rent in ${areaName} is ${displayRent}. This is <strong>${numberUtils.asCurrency(
                Math.abs(medianRent - medianNationalRent)
            )}</strong> ${moreOrLess(medianRent - medianNationalRent)} than the national median.`
        }
    ].filter((qAndA): qAndA is { question: string; answer: string } => !!qAndA);
}

const MarketFAQ: React.FunctionComponent<MarketFAQProps> = (props) => {
    const { marketStats, isMobile } = props;
    const { areaName } = marketStats;
    const FAQ = useMemo(() => getFAQ(marketStats), [marketStats]);
    return areaName && FAQ.length ? (
        <Card cardType="secondary" padding="md" paddingBottom="xl" marginTop="md">
            <Heading level="4" as="h3" fontFamily="serif" marginBottom="lg">
                Frequently Asked Questions
            </Heading>
            {FAQ.map(({ question, answer }, i) => (
                <QuestionAnswer key={question} question={question} answer={answer} i={i} isMobile={isMobile} />
            ))}
        </Card>
    ) : null;
};

export default MarketFAQ;
